import {
    useCallback,
    useEffect,
    useRef,
    useState
} from 'react'

const BURST_RESET_AFTER_MS = 4000

const FREE_MESSAGES = 6

const getRestrictionCooldownMs = (
    restrictionCount: number
) => {
    if (restrictionCount === 1) return 2000
    if (restrictionCount === 2) return 5000
    if (restrictionCount === 3) return 10000
    if (restrictionCount === 4) return 20000

    return 30000
}

export function useChatCooldown() {
    const burstCountRef = useRef(0)

    // This survives normal burst resets.
    const restrictionCountRef = useRef(0)

    const lastAcceptedSendRef = useRef(0)
    const cooldownUntilRef = useRef(0)

    const cooldownTimerRef =
        useRef<number | null>(null)

    const [isChatCooldown, setIsChatCooldown] =
        useState(false)

    const [cooldownRemainingMs, setCooldownRemainingMs] =
        useState(0)

    const clearCooldownTimer = useCallback(() => {
        if (cooldownTimerRef.current !== null) {
            window.clearTimeout(
                cooldownTimerRef.current
            )

            cooldownTimerRef.current = null
        }
    }, [])

    const beginCooldown = useCallback(
        (cooldownMs: number) => {
            clearCooldownTimer()

            const until =
                Date.now() + cooldownMs

            cooldownUntilRef.current = until

            setIsChatCooldown(true)
            setCooldownRemainingMs(cooldownMs)

            cooldownTimerRef.current =
                window.setTimeout(() => {
                    cooldownUntilRef.current = 0

                    setIsChatCooldown(false)
                    setCooldownRemainingMs(0)

                    cooldownTimerRef.current = null
                }, cooldownMs)
        },
        [clearCooldownTimer]
    )

    const trySend = useCallback(() => {
        const now = Date.now()

        // Already restricted.
        if (
            cooldownUntilRef.current > 0 &&
            now < cooldownUntilRef.current
        ) {
            const remainingMs =
                cooldownUntilRef.current - now

            setCooldownRemainingMs(
                remainingMs
            )

            return {
                allowed: false,
                restricted: true,
                remainingMs,
                restrictionCount:
                    restrictionCountRef.current
            }
        }

        // Burst inactivity resets ONLY the burst.
        // It does NOT forgive previous restrictions.
        if (
            lastAcceptedSendRef.current === 0 ||
            now - lastAcceptedSendRef.current >
                BURST_RESET_AFTER_MS
        ) {
            burstCountRef.current = 0
        }

        burstCountRef.current += 1
        lastAcceptedSendRef.current = now

        // First 5 messages in a burst are allowed.
        if (
            burstCountRef.current <=
            FREE_MESSAGES
        ) {
            return {
                allowed: true,
                restricted: false,
                remainingMs: 0,
                burstCount:
                    burstCountRef.current,
                restrictionCount:
                    restrictionCountRef.current
            }
        }

        // User crossed the burst limit.
        restrictionCountRef.current += 1

        const cooldownMs =
            getRestrictionCooldownMs(
                restrictionCountRef.current
            )

        // Start a fresh burst after restriction.
        burstCountRef.current = 0

        beginCooldown(
            cooldownMs
        )

        return {
            allowed: false,
            restricted: true,
            remainingMs: cooldownMs,
            burstCount: 0,
            restrictionCount:
                restrictionCountRef.current
        }
    }, [beginCooldown])

    useEffect(() => {
        return () => {
            clearCooldownTimer()
        }
    }, [clearCooldownTimer])

    return {
        trySend,
        isChatCooldown,
        cooldownRemainingMs
    }
}